import { forwardRef } from 'react';
import { Flex } from '@chakra-ui/react';
import type { FlexProps } from '@chakra-ui/react';
import { Edit03, Trash01 } from '@kula-ai/icons-core';

interface ContactEditWrapperProps extends FlexProps {
  onOpen?: () => void;
  showDelete?: boolean;
  handleDelete?: () => void;
  disableEdit?: boolean;
}

/**
 * Hoverable row that reveals edit/delete affordances for one contact field.
 *
 * Forwards its ref and any extra props to the row element, so it can be a
 * Radix `asChild` trigger. Without that, Radix's injected click handler and
 * aria attributes are dropped and the popover never opens.
 */
export const ContactEditWrapper = forwardRef<
  HTMLDivElement,
  ContactEditWrapperProps
>(
  (
    {
      children,
      onOpen,
      showDelete = false,
      handleDelete = () => {},
      disableEdit = false,
      onClick,
      ...rest
    },
    ref
  ) => (
    <Flex
      ref={ref}
      p="4px 16px"
      _hover={{ bg: !disableEdit ? 'ats-bg-primary' : '' }}
      cursor={!disableEdit ? 'pointer' : 'unset'}
      flex="1"
      borderRadius="6px"
      alignItems="center"
      justifyContent="space-between"
      role="group"
      gap="12px"
      onClick={(event) => {
        if (disableEdit) return;
        onClick?.(event);
        onOpen?.();
      }}
      {...rest}
    >
      {children}
      <Flex
        gap="6px"
        display="none"
        _groupHover={{ display: !disableEdit ? 'flex' : 'none' }}
      >
        <Edit03 size="1em" className="kula-icon" />
        {showDelete && (
          <Trash01
            size="1em"
            className="kula-icon"
            onClick={(event) => {
              event.stopPropagation();
              handleDelete();
            }}
          />
        )}
      </Flex>
    </Flex>
  )
);

ContactEditWrapper.displayName = 'ContactEditWrapper';
